import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import { motion, AnimatePresence } from "framer-motion";

import Dialog from "src/components/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { getTransactions } from "src/store/transaction/selector";
import {
  setTransactionsReviewed,
  batchUpdateTransactionAccountIds,
  batchUpdateTransactionVendors,
  deleteTransactions,
} from "src/apiService/modules/transactions";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import type { TransactionWithDetails } from "src/interfaces/transaction.interface";
import ReviewQueueStatus from "src/components/ReviewQueueStatus";
import { useReviewQueueLoader } from "src/utils/userData/useReviewQueueLoader";

import { useDialogStyles } from "./styles";
import TransactionCategoryCard, {
  type TransactionGroup,
  type ApprovalState,
} from "./TransactionCategoryCard";
import RecategorizeDialog from "./RecategorizeDialog";
import VendorAssignDialog from "./VendorAssignDialog";
import ConfirmDialog from "src/components/ConfirmDialog";
import WalkThrough from "./WalkThrough";

// Group transactions by GL account
function groupTransactionsByAccount(
  transactions: TransactionWithDetails[]
): TransactionGroup[] {
  const groups: Record<string, TransactionGroup> = {};

  for (const transaction of transactions) {
    const key = transaction.gl_account_id || "uncategorized";
    if (!groups[key]) {
      groups[key] = {
        accountId: key,
        accountName: transaction.account || "Uncategorized",
        transactions: [],
        total: 0,
      };
    }
    groups[key].transactions.push(transaction);
    groups[key].total += transaction.amount;
  }

  // Sort groups by transaction count (most transactions first)
  return Object.values(groups).sort(
    (a, b) => b.transactions.length - a.transactions.length
  );
}

function CategorizeExpensesDialogContent({
  onClose,
  ready,
  queueLoading,
  queueError,
  onRetry,
}: {
  onClose: () => void;
  ready: boolean;
  queueLoading: boolean;
  queueError: unknown;
  onRetry: () => void;
}) {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const transactionState = useSelector(getTransactions);
  const classes = useDialogStyles();

  // Track which card is expanded (accordion behavior)
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Track pending card to open after current one closes
  const [pendingExpandId, setPendingExpandId] = useState<string | null>(null);

  // Track selected transactions per card: { [accountId]: [transactionId, ...] }
  const [selectedByCard, setSelectedByCard] = useState<
    Record<string, string[]>
  >({});

  // Track completed cards (cards where all transactions have been approved)
  const [completedCards, setCompletedCards] = useState<Set<string>>(new Set());

  // Loading state for API calls
  const [loading, setLoading] = useState(false);

  // Track approval state per card: { [cardId]: "idle" | "loading" | "success" }
  const [cardApprovalState, setCardApprovalState] = useState<
    Record<string, ApprovalState>
  >({});

  // Track approved count per card for success message
  const [cardApprovedCount, setCardApprovedCount] = useState<
    Record<string, number>
  >({});

  // Preserve card data during approval animation
  const [preservedCards, setPreservedCards] = useState<
    Record<string, { group: TransactionGroup; position: number }>
  >({});

  // Recategorize dialog state
  const [recategorizeState, setRecategorizeState] = useState<{
    open: boolean;
    cardId: string;
    transactionIds: string[];
  }>({
    open: false,
    cardId: "",
    transactionIds: [],
  });

  // Delete confirmation dialog state
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    cardId: string;
    transactionIds: string[];
  }>({
    open: false,
    cardId: "",
    transactionIds: [],
  });

  // Vendor assign dialog state
  const [vendorAssignState, setVendorAssignState] = useState<
    { open: true; cardId: string; transactionIds: string[] } | { open: false }
  >({ open: false });

  // Total count of all unreviewed transactions (for display)
  const totalUnreviewedCount = transactionState.unreviewedTransactions.length;

  // Group transactions by account
  const groupedTransactions = useMemo(
    () => groupTransactionsByAccount(transactionState.unreviewedTransactions),
    [transactionState.unreviewedTransactions]
  );

  // Track stable card order - cards maintain their position once they appear
  const prevOrderRef = useRef<string[]>([]);
  const hadUnreviewedTransactionsRef = useRef(false);

  const stableCardOrder = useMemo(() => {
    const currentIds = groupedTransactions.map((g) => g.accountId);
    const preservedIds = Object.keys(preservedCards);
    const prevOrder = prevOrderRef.current;

    const existingInOrder = prevOrder.filter(
      (id) => currentIds.includes(id) || preservedIds.includes(id)
    );

    const newIds = currentIds.filter((id) => !prevOrder.includes(id));

    let result: string[];
    if (newIds.length > 0) {
      if (prevOrder.length === 0) {
        result = currentIds;
      } else {
        result = [...existingInOrder, ...newIds];
      }
    } else {
      result = existingInOrder;
    }

    prevOrderRef.current = result;
    return result;
  }, [groupedTransactions, preservedCards]);

  // Build the list of cards to render with stable ordering
  const cardsToRender = useMemo(() => {
    // Create a map for quick lookup
    const groupMap = new Map(groupedTransactions.map((g) => [g.accountId, g]));

    // Build cards in stable order
    const cards: TransactionGroup[] = [];
    for (const accountId of stableCardOrder) {
      const group = groupMap.get(accountId) || preservedCards[accountId]?.group;
      if (group) {
        cards.push(group);
      }
    }

    // Filter out completed cards
    return cards.filter((group) => !completedCards.has(group.accountId));
  }, [groupedTransactions, stableCardOrder, preservedCards, completedCards]);

  // Initialize selected state when groups change
  useEffect(() => {
    const newSelected: Record<string, string[]> = {};
    for (const group of groupedTransactions) {
      if (selectedByCard[group.accountId] === undefined) {
        newSelected[group.accountId] = group.transactions.map((t) => t.id);
      } else {
        newSelected[group.accountId] = selectedByCard[group.accountId];
      }
    }
    setSelectedByCard(newSelected);
  }, [groupedTransactions]);

  // Clear stale state for cards that reappear
  useEffect(() => {
    const currentAccountIds = new Set(
      groupedTransactions.map((g) => g.accountId)
    );

    setCompletedCards((current) => {
      const updated = new Set(current);
      let hasChanges = false;
      for (const accountId of current) {
        if (currentAccountIds.has(accountId)) {
          updated.delete(accountId);
          hasChanges = true;
        }
      }
      return hasChanges ? updated : current;
    });

    setCardApprovalState((current) => {
      const updated = { ...current };
      let hasChanges = false;
      for (const accountId of Object.keys(current)) {
        if (currentAccountIds.has(accountId) && current[accountId] !== "idle") {
          updated[accountId] = "idle";
          hasChanges = true;
        }
      }
      return hasChanges ? updated : current;
    });
  }, [groupedTransactions]);

  // Close once all previously-unreviewed transactions have been reviewed.
  useEffect(() => {
    if (queueLoading || queueError) return;
    if (totalUnreviewedCount > 0) {
      hadUnreviewedTransactionsRef.current = true;
      return;
    }

    const hasPreservedCards = Object.keys(preservedCards).length > 0;
    if (hadUnreviewedTransactionsRef.current && !hasPreservedCards) {
      dispatch(setActiveDialog(""));
    }
  }, [
    queueLoading,
    queueError,
    totalUnreviewedCount,
    preservedCards,
    dispatch,
  ]);

  const handleToggleExpand = (cardId: string) => {
    setExpandedCardId((current) => {
      if (current === cardId) {
        return null;
      } else if (current !== null) {
        setPendingExpandId(cardId);
        return null;
      } else {
        return cardId;
      }
    });
  };

  const handleCollapseComplete = () => {
    if (pendingExpandId && expandedCardId === null) {
      setExpandedCardId(pendingExpandId);
      setPendingExpandId(null);
    }
  };

  const handleExitComplete = () => {
    setPreservedCards((current) => {
      const updated = { ...current };
      for (const cardId of completedCards) {
        delete updated[cardId];
      }
      return updated;
    });
  };

  const handleToggleSelect = (cardId: string, transactionId: string) => {
    setSelectedByCard((current) => {
      const cardSelected = current[cardId] || [];
      const isSelected = cardSelected.includes(transactionId);
      return {
        ...current,
        [cardId]: isSelected
          ? cardSelected.filter((id) => id !== transactionId)
          : [...cardSelected, transactionId],
      };
    });
  };

  const handleSelectAll = (cardId: string) => {
    const group =
      groupedTransactions.find((g) => g.accountId === cardId) ||
      preservedCards[cardId]?.group;
    if (group) {
      setSelectedByCard((current) => ({
        ...current,
        [cardId]: group.transactions.map((t) => t.id),
      }));
    }
  };

  const handleDeselectAll = (cardId: string) => {
    setSelectedByCard((current) => ({
      ...current,
      [cardId]: [],
    }));
  };

  const handleApprove = async (cardId: string, explicitIds?: string[]) => {
    const transactionIds = explicitIds || selectedByCard[cardId] || [];
    if (transactionIds.length === 0) return;

    const group =
      groupedTransactions.find((g) => g.accountId === cardId) ||
      preservedCards[cardId]?.group;
    if (!group) return;

    const allApproved = transactionIds.length === group.transactions.length;

    setLoading(true);
    setCardApprovalState((current) => ({ ...current, [cardId]: "loading" }));

    if (allApproved) {
      const position = groupedTransactions.findIndex(
        (g) => g.accountId === cardId
      );
      setPreservedCards((current) => ({
        ...current,
        [cardId]: { group, position: position >= 0 ? position : 0 },
      }));
    }

    try {
      setTransactionsReviewed(transactionIds).catch(() => {
        toast.error("Failed to sync approval. Changes may not be saved.");
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));

      setCardApprovedCount((current) => ({
        ...current,
        [cardId]: transactionIds.length,
      }));
      setCardApprovalState((current) => ({
        ...current,
        [cardId]: "success",
      }));

      await new Promise((resolve) => setTimeout(resolve, 1500));

      setLoading(false);

      if (allApproved) {
        setExpandedCardId(null);
        setCompletedCards((current) => new Set([...current, cardId]));
        setCardApprovalState((current) => ({ ...current, [cardId]: "idle" }));
      } else {
        setCardApprovalState((current) => ({ ...current, [cardId]: "idle" }));
      }

      setSelectedByCard((current) => ({
        ...current,
        [cardId]: [],
      }));
    } catch (e) {
      toast.error("Failed to approve transactions. Please try again.");
      setLoading(false);
      setCardApprovalState((current) => ({ ...current, [cardId]: "idle" }));
      setPreservedCards((current) => {
        const updated = { ...current };
        delete updated[cardId];
        return updated;
      });
    }
  };

  const handleOpenRecategorize = (cardId: string) => {
    const transactionIds = selectedByCard[cardId] || [];
    if (transactionIds.length === 0) return;

    setRecategorizeState({
      open: true,
      cardId,
      transactionIds,
    });
  };

  const handleCloseRecategorize = () => {
    setRecategorizeState({
      open: false,
      cardId: "",
      transactionIds: [],
    });
  };

  const handleConfirmRecategorize = (
    newAccountId: string,
    newAccountName: string
  ) => {
    const { transactionIds, cardId } = recategorizeState;
    if (transactionIds.length === 0) return;

    setSelectedByCard((current) => ({
      ...current,
      [cardId]: [],
    }));

    setCompletedCards((current) => {
      const updated = new Set(current);
      updated.delete(newAccountId);
      return updated;
    });

    batchUpdateTransactionAccountIds(
      transactionIds,
      newAccountId,
      newAccountName
    ).catch(() => {
      toast.error("Failed to sync recategorization. Changes may not be saved.");
    });

    handleCloseRecategorize();
    toast.success(
      `${transactionIds.length} transaction${
        transactionIds.length !== 1 ? "s" : ""
      } recategorized to ${newAccountName}`
    );
    recordEvent({
      type: "feature-adoption",
      feature: "bulk-edit-in-app",
      element: "approve-transactions",
      count: transactionIds.length,
    });
  };

  const handleOpenDelete = (cardId: string) => {
    const transactionIds = selectedByCard[cardId] || [];
    if (transactionIds.length === 0) return;

    setDeleteState({
      open: true,
      cardId,
      transactionIds,
    });
  };

  const handleCloseDelete = () => {
    setDeleteState({
      open: false,
      cardId: "",
      transactionIds: [],
    });
  };

  const handleConfirmDelete = async () => {
    const { transactionIds, cardId } = deleteState;
    if (transactionIds.length === 0) return;

    setLoading(true);
    try {
      await deleteTransactions(transactionIds);
      setSelectedByCard((current) => ({
        ...current,
        [cardId]: [],
      }));
      toast.success(
        `${transactionIds.length} transaction${
          transactionIds.length !== 1 ? "s" : ""
        } deleted`
      );
      recordEvent({
        type: "feature-adoption",
        feature: "bulk-delete",
        element: "approve-transactions",
        count: transactionIds.length,
      });
    } catch (e) {
      toast.error("Failed to delete transactions. Please try again.");
    } finally {
      setLoading(false);
      handleCloseDelete();
    }
  };

  const handleOpenVendorAssign = (cardId: string) => {
    const transactionIds = selectedByCard[cardId] || [];
    if (transactionIds.length === 0) return;

    setVendorAssignState({
      open: true,
      cardId,
      transactionIds,
    });
  };

  const handleCloseVendorAssign = () => {
    setVendorAssignState({ open: false });
  };

  const handleConfirmVendorAssign = (vendorId?: string) => {
    if (!vendorAssignState.open) return;
    const { transactionIds, cardId } = vendorAssignState;

    setSelectedByCard((current) => ({
      ...current,
      [cardId]: [],
    }));

    handleCloseVendorAssign();

    batchUpdateTransactionVendors(transactionIds, vendorId)
      .then(() => {
        toast.success(
          `${transactionIds.length} transaction${
            transactionIds.length !== 1 ? "s" : ""
          } vendor updated`
        );
        recordEvent({
          type: "feature-adoption",
          feature: "bulk-vendor-assign",
          element: "approve-transactions",
          count: transactionIds.length,
        });
      })
      .catch(() => {
        toast.error("Failed to sync vendor change. Changes may not be saved.");
      });
  };

  // Ref to store callback for when XGrid mounts
  const onXGridMountedCallbackRef = useRef<(() => void) | null>(null);

  if (queueLoading || queueError) {
    return (
      <>
        <DialogTitle>Approve Transactions</DialogTitle>
        <ReviewQueueStatus
          loading={queueLoading}
          error={queueError}
          onRetry={onRetry}
          label="transactions to review"
        />
      </>
    );
  }

  return (
    <>
      <WalkThrough
        ready={ready}
        onExpandFirstCard={(onReady) => {
          const firstCardId = cardsToRender[0]?.accountId;
          if (firstCardId && expandedCardId !== firstCardId) {
            onXGridMountedCallbackRef.current = onReady;
            setExpandedCardId(firstCardId);
          } else if (firstCardId && expandedCardId === firstCardId) {
            // Already expanded, call immediately
            onReady();
          }
        }}
      />
      <RecategorizeDialog
        open={recategorizeState.open}
        transactionCount={recategorizeState.transactionIds.length}
        currentAccountId={recategorizeState.cardId}
        onClose={handleCloseRecategorize}
        onConfirm={handleConfirmRecategorize}
      />
      <VendorAssignDialog
        open={vendorAssignState.open}
        transactionCount={
          vendorAssignState.open ? vendorAssignState.transactionIds.length : 0
        }
        onClose={handleCloseVendorAssign}
        onConfirm={handleConfirmVendorAssign}
      />
      <ConfirmDialog
        open={deleteState.open}
        title="Delete Transactions"
        text={`Are you sure you want to delete ${
          deleteState.transactionIds.length
        } transaction${
          deleteState.transactionIds.length !== 1 ? "s" : ""
        }? This action cannot be undone.`}
        onCancel={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        cancel="Cancel"
        confirm="Delete"
      />

      <DialogTitle>
        Approve Transactions
        <Typography variant="body2" color="textSecondary">
          {totalUnreviewedCount} transaction
          {totalUnreviewedCount !== 1 ? "s" : ""} to review
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <motion.div className={classes.cardGrid} layout>
          <AnimatePresence onExitComplete={handleExitComplete}>
            {cardsToRender.map((group, index) => (
              <motion.div
                key={group.accountId}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{
                  opacity: { duration: 0.25 },
                  scale: { duration: 0.25 },
                  layout: { type: "spring", stiffness: 150, damping: 22 },
                }}
                data-tour={index === 0 ? "category-card" : undefined}
                onLayoutAnimationComplete={
                  index === 0
                    ? () => {
                        if (onXGridMountedCallbackRef.current) {
                          onXGridMountedCallbackRef.current();
                          onXGridMountedCallbackRef.current = null;
                        }
                      }
                    : undefined
                }
              >
                <TransactionCategoryCard
                  group={preservedCards[group.accountId]?.group || group}
                  expanded={expandedCardId === group.accountId}
                  completed={false}
                  approvalState={cardApprovalState[group.accountId] || "idle"}
                  approvedCount={cardApprovedCount[group.accountId] || 0}
                  selectedIds={selectedByCard[group.accountId] || []}
                  onToggleExpand={() => handleToggleExpand(group.accountId)}
                  onToggleSelect={(id) =>
                    handleToggleSelect(group.accountId, id)
                  }
                  onSelectAll={() => handleSelectAll(group.accountId)}
                  onDeselectAll={() => handleDeselectAll(group.accountId)}
                  onApprove={(ids) => handleApprove(group.accountId, ids)}
                  onRecategorize={() => handleOpenRecategorize(group.accountId)}
                  onVendorAssign={() => handleOpenVendorAssign(group.accountId)}
                  onDelete={() => handleOpenDelete(group.accountId)}
                  onCollapseComplete={handleCollapseComplete}
                  loading={loading}
                  isFirstCard={index === 0}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {cardsToRender.length === 0 && (
            <Typography className={classes.emptyState}>
              No transactions to review
            </Typography>
          )}
        </motion.div>
      </DialogContent>
    </>
  );
}

function CategorizeExpensesDialog({ open }: { open: boolean }) {
  const classes = useDialogStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const queue = useReviewQueueLoader(open, "transactions");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setReady(false);
    }
  }, [open]);

  const handleClose = () => {
    dispatch(setActiveDialog(""));
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      maxWidth={false}
      disableEnforceFocus
      classes={{ paper: classes.paper }}
      TransitionProps={{ onEntered: () => setReady(true) }}
    >
      {open && (
        <CategorizeExpensesDialogContent
          onClose={handleClose}
          ready={ready}
          queueLoading={queue.loading}
          queueError={queue.error}
          onRetry={queue.retry}
        />
      )}
    </Dialog>
  );
}

export default CategorizeExpensesDialog;
